import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Save, Upload, Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import type { SustainabilityCard } from "@/lib/sustainability.functions";

export const Route = createFileRoute("/_authenticated/admin/duurzaamheid")({
  head: () => ({
    meta: [
      { title: "Admin — Duurzaamheid beheren" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminSustainabilityPage,
});

const ICON_OPTIONS = [
  "Leaf",
  "TreePine",
  "Truck",
  "Recycle",
  "Lightbulb",
  "Moon",
  "Sun",
  "Droplets",
  "Bike",
  "Sprout",
  "Wind",
  "Heart",
];

type Row = {
  title: string;
  intro: string;
  body: string;
  hero_path: string | null;
  hero_alt: string;
  seo_title: string;
  seo_description: string;
  og_description: string;
  cta_primary_label: string;
  cta_primary_url: string;
  cta_secondary_label: string;
  cta_secondary_url: string;
  cards: SustainabilityCard[];
};

function AdminSustainabilityPage() {
  const [row, setRow] = useState<Row | null>(null);
  const [saving, setSaving] = useState(false);
  const [heroPreview, setHeroPreview] = useState<string | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("sustainability_page")
      .select("*")
      .eq("id", "default")
      .maybeSingle();
    if (error) return toast.error(error.message);
    const record: Row = {
      title: data?.title ?? "Zo groen mogelijk",
      intro: data?.intro ?? "",
      body: data?.body ?? "",
      hero_path: (data?.hero_path as string | null) ?? null,
      hero_alt: data?.hero_alt ?? "",
      seo_title: data?.seo_title ?? "",
      seo_description: data?.seo_description ?? "",
      og_description: data?.og_description ?? "",
      cta_primary_label: data?.cta_primary_label ?? "",
      cta_primary_url: data?.cta_primary_url ?? "",
      cta_secondary_label: data?.cta_secondary_label ?? "",
      cta_secondary_url: data?.cta_secondary_url ?? "",
      cards: ((data?.cards as SustainabilityCard[] | null) ?? []),
    };
    setRow(record);
    if (record.hero_path) {
      const { data: signed } = await supabase.storage
        .from("page-media")
        .createSignedUrl(record.hero_path, 60 * 60);
      setHeroPreview(signed?.signedUrl ?? null);
    } else {
      setHeroPreview(null);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function patch(p: Partial<Row>) {
    setRow((r) => (r ? { ...r, ...p } : r));
  }

  function patchCard(idx: number, p: Partial<SustainabilityCard>) {
    setRow((r) =>
      r ? { ...r, cards: r.cards.map((c, i) => (i === idx ? { ...c, ...p } : c)) } : r,
    );
  }

  function moveCard(idx: number, dir: -1 | 1) {
    setRow((r) => {
      if (!r) return r;
      const target = idx + dir;
      if (target < 0 || target >= r.cards.length) return r;
      const cards = [...r.cards];
      [cards[idx], cards[target]] = [cards[target], cards[idx]];
      return { ...r, cards };
    });
  }

  function addCard() {
    setRow((r) =>
      r
        ? {
            ...r,
            cards: [
              ...r.cards,
              {
                id: Math.random().toString(36).slice(2, 9),
                icon: "Leaf",
                title: "Nieuw kaartje",
                text: "",
                visible: true,
              },
            ],
          }
        : r,
    );
  }

  function removeCard(idx: number) {
    setRow((r) => (r ? { ...r, cards: r.cards.filter((_, i) => i !== idx) } : r));
  }

  async function uploadHero(file: File) {
    const ext = file.name.split(".").pop() || "jpg";
    const path = `duurzaamheid/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const up = await supabase.storage.from("page-media").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (up.error) return toast.error("Upload mislukt");
    patch({ hero_path: path });
    const { data: signed } = await supabase.storage
      .from("page-media")
      .createSignedUrl(path, 60 * 60);
    setHeroPreview(signed?.signedUrl ?? null);
    toast.success("Afbeelding geüpload — vergeet niet op te slaan");
  }

  async function save() {
    if (!row) return;
    setSaving(true);
    const { error } = await supabase
      .from("sustainability_page")
      .update({
        title: row.title,
        intro: row.intro,
        body: row.body,
        hero_path: row.hero_path,
        hero_alt: row.hero_alt,
        seo_title: row.seo_title,
        seo_description: row.seo_description,
        og_description: row.og_description,
        cta_primary_label: row.cta_primary_label,
        cta_primary_url: row.cta_primary_url,
        cta_secondary_label: row.cta_secondary_label,
        cta_secondary_url: row.cta_secondary_url,
        cards: row.cards as unknown as never,
      })
      .eq("id", "default");
    setSaving(false);
    if (error) return toast.error("Opslaan mislukt");
    toast.success("Opgeslagen");
  }

  if (!row) {
    return <div className="px-5 py-10 text-sm text-foreground/60 md:px-10">Laden…</div>;
  }

  return (
    <div className="px-5 py-10 md:px-10 md:py-14">
      <Toaster position="top-center" />
      <div className="mx-auto max-w-4xl space-y-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">
              — Duurzaamheid
            </p>
            <h1 className="mt-3 font-display text-3xl md:text-4xl">Pagina beheren</h1>
          </div>
          <Button onClick={save} disabled={saving} variant="hero">
            <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>

        <section className="space-y-4 rounded-2xl border border-border bg-night-deep/40 p-6">
          <h2 className="font-display text-xl">Hero</h2>
          <div>
            <Label htmlFor="title">Titel</Label>
            <Input id="title" value={row.title} onChange={(e) => patch({ title: e.target.value })} />
          </div>
          <div>
            <Label>Intro</Label>
            <RichTextEditor value={row.intro} onChange={(v) => patch({ intro: v })} />
          </div>
          <div className="space-y-2">
            <Label>Hero-afbeelding</Label>
            {heroPreview && (
              <img
                src={heroPreview}
                alt=""
                className="aspect-[16/9] w-full max-w-md rounded-xl border border-border object-cover"
              />
            )}
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:border-gold/50">
                <Upload className="h-4 w-4" /> Uploaden
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadHero(f);
                    e.target.value = "";
                  }}
                />
              </label>
              {row.hero_path && (
                <Button
                  variant="ghostLine"
                  size="sm"
                  onClick={() => {
                    patch({ hero_path: null });
                    setHeroPreview(null);
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Verwijderen
                </Button>
              )}
            </div>
            <Input
              placeholder="Alt-tekst"
              value={row.hero_alt}
              onChange={(e) => patch({ hero_alt: e.target.value })}
            />
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-night-deep/40 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Kaartjes</h2>
            <Button variant="ghostLine" size="sm" onClick={addCard}>
              <Plus className="h-4 w-4" /> Kaartje
            </Button>
          </div>
          <ul className="space-y-4">
            {row.cards.map((card, idx) => (
              <li key={card.id} className="space-y-3 rounded-xl border border-border p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    aria-label="Icoon"
                    value={card.icon}
                    onChange={(e) => patchCard(idx, { icon: e.target.value })}
                    className="rounded-md border border-border bg-background px-2 py-2 text-sm"
                  >
                    {ICON_OPTIONS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                  <Input
                    className="min-w-[12rem] flex-1"
                    value={card.title}
                    placeholder="Titel"
                    onChange={(e) => patchCard(idx, { title: e.target.value })}
                  />
                  <label className="flex items-center gap-2 text-xs text-foreground/70">
                    <Switch
                      checked={card.visible !== false}
                      onCheckedChange={(v) => patchCard(idx, { visible: v })}
                    />
                    Zichtbaar
                  </label>
                  <div className="flex items-center gap-1">
                    <Button variant="ghostLine" size="icon" aria-label="Omhoog" onClick={() => moveCard(idx, -1)}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghostLine" size="icon" aria-label="Omlaag" onClick={() => moveCard(idx, 1)}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghostLine" size="icon" aria-label="Verwijderen" onClick={() => removeCard(idx)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <Textarea
                  rows={2}
                  placeholder="Tekst"
                  value={card.text}
                  onChange={(e) => patchCard(idx, { text: e.target.value })}
                />
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-night-deep/40 p-6">
          <h2 className="font-display text-xl">Uitgebreide tekst</h2>
          <RichTextEditor value={row.body} onChange={(v) => patch({ body: v })} />
        </section>

        <section className="grid gap-4 rounded-2xl border border-border bg-night-deep/40 p-6 sm:grid-cols-2">
          <h2 className="font-display text-xl sm:col-span-2">Actieknoppen</h2>
          <div>
            <Label>Knop 1 — label</Label>
            <Input
              value={row.cta_primary_label}
              onChange={(e) => patch({ cta_primary_label: e.target.value })}
            />
          </div>
          <div>
            <Label>Knop 1 — link</Label>
            <Input
              value={row.cta_primary_url}
              onChange={(e) => patch({ cta_primary_url: e.target.value })}
            />
          </div>
          <div>
            <Label>Knop 2 — label</Label>
            <Input
              value={row.cta_secondary_label}
              onChange={(e) => patch({ cta_secondary_label: e.target.value })}
            />
          </div>
          <div>
            <Label>Knop 2 — link</Label>
            <Input
              value={row.cta_secondary_url}
              onChange={(e) => patch({ cta_secondary_url: e.target.value })}
            />
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-night-deep/40 p-6">
          <h2 className="font-display text-xl">SEO</h2>
          <div>
            <Label>Meta-titel</Label>
            <Input value={row.seo_title} onChange={(e) => patch({ seo_title: e.target.value })} />
          </div>
          <div>
            <Label>Meta-omschrijving</Label>
            <Textarea
              rows={2}
              value={row.seo_description}
              onChange={(e) => patch({ seo_description: e.target.value })}
            />
          </div>
          <div>
            <Label>Social (og) omschrijving</Label>
            <Textarea
              rows={2}
              value={row.og_description}
              onChange={(e) => patch({ og_description: e.target.value })}
            />
          </div>
        </section>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} variant="hero">
            <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>
      </div>
    </div>
  );
}